/**
 * AiChatCliRequiredDialog — Shown when no AI CLI is installed.
 *
 * Explains that the AI Chat feature requires either Claude Code CLI or
 * Codex CLI, and provides links to install each.
 */

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { ExternalLink } from 'lucide-react';

type AiChatCliRequiredDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const CLAUDE_CLI_INSTALL_URL = 'https://docs.anthropic.com/en/docs/claude-code/overview';
const CODEX_CLI_INSTALL_URL = 'https://github.com/openai/codex';

export function AiChatCliRequiredDialog({ open, onOpenChange }: AiChatCliRequiredDialogProps) {
  const handleOpenClaudeInstall = () => {
    window.electronAPI?.openExternalUrl(CLAUDE_CLI_INSTALL_URL);
  };

  const handleOpenCodexInstall = () => {
    window.electronAPI?.openExternalUrl(CODEX_CLI_INSTALL_URL);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="ai-chat-cli-required-dialog">
        <DialogHeader>
          <DialogTitle>AI CLI Required</DialogTitle>
          <DialogDescription>
            The AI Chat feature requires an AI CLI installed on your machine.
            Install either{' '}
            <span className="font-medium text-foreground">Claude Code CLI</span> or{' '}
            <span className="font-medium text-foreground">Codex CLI</span> to get started.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2 text-sm text-muted-foreground">
          Both CLIs run locally on your machine. No additional API keys or
          subscriptions need to be configured in Jam SQL — it uses your existing
          CLI setup.
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button variant="outline" onClick={handleOpenCodexInstall} data-testid="install-codex-link">
            <ExternalLink className="h-4 w-4 mr-2" />
            Install Codex
          </Button>
          <Button onClick={handleOpenClaudeInstall} data-testid="install-claude-link">
            <ExternalLink className="h-4 w-4 mr-2" />
            Install Claude Code
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
